// The portfolio as four sections — Kanosei Index, Stock Investments, Crypto,
// Private Investments — and the one summary every section shows: Starting
// Value, Current Value, P/L, over a lookback window.
//
// Starting Value is what the section was worth when the window opened PLUS
// what was put in during it, so P/L = Current − Starting holds in every cell
// and a deposit never reads as a gain. For All it is the total put in.
//
// A window is offered only where the data can answer it. The brokers' net
// deposits are known for month to date, year to date and the statement
// period, and nowhere else — a NAV series cannot tell a deposit from a gain —
// so a rolling 3M is never derived. Crypto and the private book answer All
// only. A section shows the windows ALL its parts can answer. The limits are
// written up in Notes.

import { invested, isKnown, pnlOf } from './privateMaths';

/** Lookback windows, in display order. Keys match TIMEFRAMES so the chart can
 *  follow the same choice. */
export const SUMMARY_WINDOWS = [
    { key: 'mtd', labelKey: 'timeframe.mtd', window: 'monthToDate' },
    { key: 'ytd', labelKey: 'timeframe.ytd', window: 'yearToDate' },
    { key: '1y', labelKey: 'timeframe.1y', window: 'period', year: true },
    { key: 'all', labelKey: 'timeframe.all', window: 'period' },
];

/** A finite number, or null — never NaN, never a string. */
export const n = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

/** Hand-entered Starting Value for a part's All window. */
export const startInputKey = (partKey) => `start:${partKey}`;

const isIndexAccount = (a) => a.source === 'ibkr' && a.scope === 'index';

/**
 * Which section an account belongs to.
 *
 * The non-index IBKR account is Medora Fund 1, listed under Private
 * Investments. A snapshot from before accounts carried a scope has no index
 * account at all; its IBKR accounts are the index, as they were then.
 */
export function sectionOf(account, accounts) {
    if (account.source === 'ibkr') {
        const hasIndex = accounts.some(isIndexAccount);
        return !hasIndex || isIndexAccount(account) ? 'index' : 'private';
    }
    if (account.source === 'fidelity') return 'stocks';
    if (account.source === 'crypto' || account.source === 'nft') return 'crypto';
    if (account.source === 'private') return 'private';
    return null;
}

export const SECTIONS = [
    { key: 'index', labelKey: 'nav.kanoseiIndex', path: '/holdings/kanosei-index' },
    { key: 'stocks', labelKey: 'nav.stockInvestments', path: '/holdings/stocks' },
    { key: 'crypto', labelKey: 'nav.cryptoHoldings', path: '/holdings/crypto' },
    { key: 'private', labelKey: 'nav.privateInvestments', path: '/holdings/private' },
];

const daysBetween = (from, to) =>
    (new Date(`${to}T00:00:00Z`) - new Date(`${from}T00:00:00Z`)) / 86_400_000;

/** The statement period stands in for 1Y only when it IS a year. */
const isYear = (p) => p?.fromDate && p?.toDate && Math.abs(daysBetween(p.fromDate, p.toDate) - 365) <= 7;

/**
 * A broker account's window, from the statement's own figures.
 *
 * `start` is the opening value plus net deposits; `deposits` is kept beside it
 * so the page can say how much of the start arrived inside the window.
 */
function brokerWindow(account, key, startOverride) {
    const frame = SUMMARY_WINDOWS.find((w) => w.key === key);
    const perf = account.performance?.[frame.window] ?? null;
    if (frame.year && !isYear(perf)) return null;
    const current = n(account.totalValue);
    if (key === 'all' && startOverride !== null) {
        return { start: startOverride, deposits: null, current, fromDate: perf?.fromDate ?? null, entered: true };
    }
    // With no statement behind All the value still stands; the total put in
    // is for someone to enter.
    const unknownStart = key === 'all' ? { start: null, deposits: null, current, fromDate: null } : null;
    if (!perf) return unknownStart;
    const opening = n(perf.startingValue);
    const deposits = n(perf.netDeposits) ?? 0;
    if (opening === null) return unknownStart;
    return { start: opening + deposits, deposits, current, fromDate: perf.fromDate ?? null };
}

/**
 * The parts of the book a summary is built from, each with its own figures.
 *
 * `inputs` are hand-entered Starting Values; `privateList` is the private
 * investments (for the book itself and for Medora's cost), or null when they
 * could not be read. Each part:
 * `{ key, section, labelKey|label, windows: { [key]: figures | null }, inputKey? }`.
 */
export function summaryParts(accounts = [], { inputs = {}, privateList = [] } = {}) {
    const parts = [];
    // Null when the private list could not be read, as opposed to empty.
    const list = privateList ?? [];
    const linked = new Map(
        list.filter((i) => i.linkedAccountId && i.status === 'active')
            .map((i) => [i.linkedAccountId, i]),
    );

    accounts.filter((a) => a.source === 'ibkr' || a.source === 'fidelity').forEach((a) => {
        const partKey = `${a.source}:${a.accountId ?? a.accountLabel}`;
        const section = sectionOf(a, accounts);
        const inv = linked.get(a.accountId);
        // Medora's total put in is its cost on the Private tab, once entered.
        const linkedCost = inv && (inv.rounds || []).length > 0 ? invested(inv) : null;
        const entered = n(inputs[startInputKey(partKey)]);
        const startOverride = linkedCost ?? entered;
        const windows = Object.fromEntries(SUMMARY_WINDOWS.map((w) => [w.key, brokerWindow(a, w.key, startOverride)]));
        // Until Medora's cost is entered its All start is unknown, as its row
        // says — not the statement period's opening, which is a different thing.
        if (inv && linkedCost === null) windows.all = { start: null, current: n(a.totalValue), fromDate: null };
        parts.push({
            key: partKey,
            section,
            label: inv?.name ?? a.accountLabel,
            windows,
            // Medora's cost is edited on its row, not here.
            inputKey: inv ? null : startInputKey(partKey),
            editPath: inv ? `/holdings/private?edit=${encodeURIComponent(inv.id)}` : SECTIONS.find((s) => s.key === section)?.path,
        });
    });

    // Coins: no ledger worth trusting yet (see CRYPTO_PNL_WIP), so the total
    // put in is entered by hand.
    const wallets = accounts.filter((a) => a.source === 'crypto');
    if (wallets.length > 0) {
        const current = wallets.reduce((s, a) => s + (n(a.totalValue) ?? 0), 0);
        parts.push({
            key: 'crypto',
            section: 'crypto',
            labelKey: 'sections.coins',
            inputKey: startInputKey('crypto'),
            editPath: '/holdings/crypto',
            windows: { all: { start: n(inputs[startInputKey('crypto')]), current, fromDate: null } },
        });
    }

    // NFTs: cost and P/L over the items whose cost and value are both known;
    // the value counts every item that has one. The rest are marked on the
    // Crypto tab, item by item.
    const nfts = accounts.find((a) => a.source === 'nft');
    if (nfts) {
        const inception = nfts.performance?.inception;
        parts.push({
            key: 'nft',
            section: 'crypto',
            labelKey: 'sections.nfts',
            windows: {
                all: {
                    start: n(inception?.capital),
                    current: n(nfts.totalValue),
                    pnl: n(inception?.netPnl),
                    fromDate: inception?.fromDate ?? null,
                },
            },
        });
    }

    // The private book, active investments only — an exit's proceeds are cash
    // elsewhere already. P/L counts what active ones have paid back, so it is
    // carried rather than left to Current − Starting.
    const book = list.filter((i) => i.status === 'active' && !i.linkedAccountId);
    const bookAccount = accounts.find((a) => a.source === 'private');
    if (privateList === null && bookAccount) {
        // The list could not be read: the snapshot's own account stands in.
        const inception = bookAccount.performance?.inception;
        parts.push({
            key: 'private',
            section: 'private',
            labelKey: 'sections.privateBook',
            windows: {
                all: {
                    start: n(inception?.capital),
                    current: n(bookAccount.totalValue),
                    pnl: n(inception?.netPnl),
                    fromDate: inception?.fromDate ?? null,
                },
            },
        });
    } else if (book.length > 0) {
        const known = book.filter(isKnown);
        const start = known.reduce((s, i) => s + invested(i), 0);
        const current = book.reduce((s, i) => s + (n(i.currentMark) ?? 0), 0);
        const pnl = known.reduce((s, i) => s + pnlOf(i), 0);
        const first = book.flatMap((i) => (i.rounds || []).map((r) => r.investedOn)).filter(Boolean).sort()[0] ?? null;
        parts.push({
            key: 'private',
            section: 'private',
            labelKey: 'sections.privateBook',
            windows: { all: { start, current, pnl, fromDate: first } },
        });
    }

    return parts;
}

/**
 * Figures for one window across several parts, or null when any part cannot
 * answer it — all or nothing, as a partial sum is a number that looks whole.
 *
 * A part with no Starting Value on All (nothing entered yet) still counts its
 * Current Value; the start and P/L are then unknown and flagged `needsInput`.
 */
export function combine(parts, key) {
    if (parts.length === 0) return null;
    const figures = parts.map((p) => p.windows[key] ?? null);
    if (figures.some((f) => f === null)) return null;

    const current = figures.reduce((s, f) => s + (f.current ?? 0), 0);
    const missing = figures.filter((f) => f.start === null).length;
    const start = missing > 0 ? null : figures.reduce((s, f) => s + f.start, 0);
    const pnl = start === null
        ? null
        : figures.reduce((s, f) => s + (f.pnl ?? ((f.current ?? 0) - f.start)), 0);
    const deposits = figures.some((f) => f.deposits) ? figures.reduce((s, f) => s + (f.deposits ?? 0), 0) : null;
    return {
        start,
        current,
        pnl,
        // Measured on what was put in, and only where that is a real sum.
        pnlPct: pnl !== null && start > 0 && start >= Math.abs(current) / 100 ? (pnl / start) * 100 : null,
        deposits,
        fromDate: figures.map((f) => f.fromDate).filter(Boolean).sort()[0] ?? null,
        needsInput: missing,
    };
}

/** The windows every part can answer, in display order. */
export const windowsAvailable = (parts) =>
    SUMMARY_WINDOWS.filter((w) => parts.length > 0 && parts.every((p) => p.windows[w.key]));

export const partsIn = (parts, section) => parts.filter((p) => p.section === section);
